<script>
    import { onMount } from "svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getAmountInCurrency,
        getNumber,
        getText,
        goToURL,
        isObjectEmpty,
        replaceURL,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        PageLoading,
        SearchInput,
        SvelteTable,
        LoadingModal,
    } from "../../Components/Common/index.svelte";

    import { isMobile, user } from "../../stores";

    import { BillingStatus } from "../../Utils/utilEnums.svelte";

    import {
        billingStatusDisplayMap,
        followUpDisplayMap,
        followUpRouteMap,
    } from "../../Utils/utilConstants.svelte";

    import { openTabForSSO } from "./helpers/ExternalGroupHelper.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    let isGettingExternalGroups = false,
        returnExternalGroupsArr = [],
        filters = {};

    let page = 0,
        pageSize = 25,
        count;

    let isRedirectingSSO = false;

    const sortableHeaderKeys = ["groupName", "domain"];

    const columns = [
        {
            key: "groupName",
            title: "Group Name",
            headerClass:
                "text-left px-3 w-[25%] sortable-header-decoration rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "domain",
            title: "Domain",
            headerClass: "text-left px-3 w-[25%] sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDisplayDomain(v.domain),
        },
        {
            key: "encounters",
            title: "Encounters",
            headerClass: "text-left px-3 w-[20%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDisplayEncounters(v.encountersByStatus),
            csvContent: (v) =>
                _getDisplayEncounters(v.encountersByStatus, true),
        },
        // {
        //     key: "followUp",
        //     title: "Follow Up",
        //     headerClass: "text-left px-3 w-[15%]",
        //     class: "text-left px-3 cursor-pointer",
        //     renderValue: (v) =>
        //         _getDisplayFollowUps(v.encountersFollowUpTotals),
        //     csvContent: (v) =>
        //         _getDisplayFollowUps(v.encountersFollowUpTotals, true),
        // },
        {
            key: "reconciliation",
            title: "Reconciliation",
            // headerClass: `text-left px-3 w-[15%] ${$user.role == 100 ? "" : "rounded-tr-xl"}`,
            headerClass: `text-left px-3 w-[30%] rounded-tr-xl`,
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDisplayReconciliation(v),
            csvContent: (v) => _getDisplayReconciliation(v, true),
        },
        // {
        //     key: "insights",
        //     title: "Payments Insights",
        //     headerClass: "text-left px-3 w-[15%] rounded-tr-xl",
        //     isHidden: $user.role != 100,
        //     class: "text-left px-3 cursor-pointer",
        //     renderValue: (v) => _getDisplayInsights(v),
        //     csvContent: (v) => _getDisplayInsights(v, true),
        // },
    ];

    onMount(() => {
        if (params.query) updateSearchParamsByQuery(params.query);

        loadExternalGroups(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();
            }
            updateURL();
        }
    };

    const loadExternalGroups = async (start, limit, params) => {
        isGettingExternalGroups = true;

        await apiMethods.getMethod
            .getExternalGroups(start, limit, params)
            .then((res) => _handleExternalGroups(res))
            .then(() => _updateHeaderSortIcon())
            .catch((err) => showError(err));

        isGettingExternalGroups = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadExternalGroups(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadExternalGroups(
            e.detail.firstDataIndex,
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadExternalGroups(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/ExternalGroups/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleClickCell = (
        { target, target: { className, dataset } },
        rowData,
        key,
    ) => {
        if (isRedirectingSSO) return;

        if (className.includes("domainUrl")) redirectToSSODomain(rowData);
        else if (className.includes("encounterUrl")) {
            const targetStatus = BillingStatus.find(
                (bs) =>
                    bs.type.toLowerCase().replace(/\s+/g, "") ==
                    dataset.status.toLowerCase().replace(/\s+/g, ""),
            ).id;

            if (!targetStatus && targetStatus != 0) return;

            redirectToSSODomain(
                rowData,
                `PatientEncounter/page=0&size=25&status=0&billingStatus=${targetStatus}`,
            );
        } else if (className.includes("followUpUrl"))
            redirectToSSODomain(rowData, `${followUpRouteMap[dataset.route]}`);
        else if (className.includes("reconcileERAUrl"))
            redirectToSSODomain(
                rowData,
                `Reconciliations/tab=ERAs&page=0&size=25&reconciled=0`,
            );
        else if (className.includes("reconcileEFTUrl"))
            redirectToSSODomain(
                rowData,
                `Reconciliations/tab=EFTs&page=0&size=25&reconciled=0`,
            );
        else goToURL(`/ExternalGroups/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;

            updateURL();

            loadExternalGroups(0, pageSize, _getSearchParams());
        }
    };

    const redirectToSSODomain = async (data, goToPage = "") => {
        const { id, domain } = data;

        if (!id) return;

        isRedirectingSSO = true;

        await openTabForSSO(id, domain, goToPage);

        isRedirectingSSO = false;
    };

    // HELPER FUNCTIONS
    const _handleExternalGroups = (res) => {
        if (res.error) showError(res.error);
        else {
            returnExternalGroupsArr = [...res.results];
            count = res.total;
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");

                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "groupName" &&
                            row.childNodes[0].data == "Group Name") ||
                        (params.sort == "domain" &&
                            row.childNodes[0].data == "Domain")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "groupNameDesc" &&
                            row.childNodes[0].data == "Group Name") ||
                        (params.sort == "domainDesc" &&
                            row.childNodes[0].data == "Domain")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _getDisplayDomain = (domain) =>
        `<div class="domainUrl underline hover:font-bold">https://${domain}</div>`;

    const _getDisplayEncounters = (
        encountersByStatus = {},
        isCsvContent = false,
    ) => {
        if (isObjectEmpty(encountersByStatus)) return "-";

        const res = [];

        for (const [key, value] of Object.entries(encountersByStatus)) {
            res.push(
                isCsvContent
                    ? `${billingStatusDisplayMap[key]}: ${getNumber(value)}`
                    : `<div class="encounterUrl leading-8 underline hover:font-bold" data-status="${key}">${billingStatusDisplayMap[key]}: ${getNumber(value)}</div>`,
            );
        }

        return res.toString().replaceAll(",", isCsvContent ? "\r\n" : "");
    };

    const _getDisplayFollowUps = (
        encountersFollowUpTotals = {},
        isCsvContent = false,
    ) => {
        if (isObjectEmpty(encountersFollowUpTotals)) return "-";

        const res = [];

        for (const [key, value] of Object.entries(encountersFollowUpTotals)) {
            res.push(
                isCsvContent
                    ? `${followUpDisplayMap[key]}: ${getNumber(value)}`
                    : `<div class="followUpUrl leading-8 underline hover:font-bold" data-route="${key}">${followUpDisplayMap[key]}: ${getNumber(value)}</div>`,
            );
        }

        return res.toString().replaceAll(",", isCsvContent ? "\r\n" : "");
    };

    const _getDisplayReconciliation = (data, isCsvContent = false) => {
        const { reconciliationERAs, reconciliationEFTs } = data;

        return isCsvContent
            ? `Reconcile ERAs: ${getNumber(reconciliationERAs)} \r\n Reconcile EFTs: ${getNumber(reconciliationEFTs)}`
            : `
                <div class="reconcileERAUrl leading-8 underline hover:font-bold">Reconcile ERAs: ${getNumber(reconciliationERAs)}</div>

                <div class="reconcileEFTUrl leading-8 underline hover:font-bold">Reconcile EFTs: ${getNumber(reconciliationEFTs)}</div>
            `;
    };

    const _getDisplayInsights = (data, isCsvContent = false) => {
        const {
            totalPaymentsPatientsPreviousMonth,
            totalPaymentsPatientsCurrentMonth,
            totalPaymentsHealthFundPreviousMonth,
            totalPaymentsHealthFundCurrentMonth,
        } = data;

        return isCsvContent
            ? `Patients: Last Month: ${getAmountInCurrency(totalPaymentsPatientsPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsPatientsCurrentMonth)} \r\n Health Fund: Last Month: ${getAmountInCurrency(totalPaymentsHealthFundPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsHealthFundCurrentMonth)} \r\n Total: Last Month: ${getAmountInCurrency(totalPaymentsPatientsPreviousMonth + totalPaymentsHealthFundPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsPatientsCurrentMonth + totalPaymentsHealthFundCurrentMonth)}`
            : `
                <div class="leading-8"><span class="font-bold">Patients: </span> Last Month: ${getAmountInCurrency(totalPaymentsPatientsPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsPatientsCurrentMonth)}</div>

                <div class="leading-8"><span class="font-bold">Health Fund: </span> Last Month: ${getAmountInCurrency(totalPaymentsHealthFundPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsHealthFundCurrentMonth)}</div>


                <div class="leading-8"><span class="font-bold">Total: </span> Last Month: ${getAmountInCurrency(totalPaymentsPatientsPreviousMonth + totalPaymentsHealthFundPreviousMonth)}, Current Month: ${getAmountInCurrency(totalPaymentsPatientsCurrentMonth + totalPaymentsHealthFundCurrentMonth)}</div>
            `;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-wrap">
        <SearchInput
            bind:value={filters.search}
            title="Search"
            inputFunc={handleFilterSelect}
            isLong
        />
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingExternalGroups}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-scroll overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnExternalGroupsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnExternalGroupsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnExternalGroupsArr,
            fileName: "External Groups",
            allDataApi: async () =>
                await apiMethods.getMethod.getExternalGroups(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if isRedirectingSSO}
    <LoadingModal titleText="Redirecting..." />
{/if}
